/**
 * PWA Install Prompt Component
 * Shows "Add to Home Screen" prompt for mobile users
 */

import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Download, X, Smartphone, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { a11yLabel } from '../utils/a11yLabels';

// Global variable to store the deferred prompt
let deferredPrompt = null;

const PWAInstallPrompt = () => {
  const { language } = useLanguage();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if previously dismissed
    const dismissedAt = localStorage.getItem('shs_pwa_dismissed');
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        setDismissed(true);
        return;
      }
    }

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      
      // Show prompt after 30 seconds of browsing
      setTimeout(() => {
        setShowPrompt(true);
      }, 30000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      deferredPrompt = null;
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    deferredPrompt = null;
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('shs_pwa_dismissed', new Date().toISOString());
  };

  if (isInstalled || dismissed || !showPrompt) return null;

  const content = {
    en: {
      title: 'Install Sleep Health Services',
      description: 'Add to your home screen for quick access to sleep assessments, AI chat, and your personalized profile.',
      features: [
        'Works offline',
        'Instant access',
        'Get notifications'
      ],
      install: 'Install App',
      notNow: 'Not Now'
    },
    es: {
      title: 'Instalar Sleep Health Services',
      description: 'Añade a tu pantalla de inicio para acceso rápido a evaluaciones de sueño, chat IA y tu perfil personalizado.',
      features: [
        'Funciona sin conexión',
        'Acceso instantáneo',
        'Recibe notificaciones'
      ],
      install: 'Instalar App',
      notNow: 'Ahora No'
    }
  };

  const t = content[language] || content.en;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96" data-testid="pwa-install-prompt">
      <Card className="shadow-2xl border-2 border-blue-200 bg-white">
        <CardContent className="p-4">
          <button 
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
            aria-label={a11yLabel("Dismiss")}
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <Smartphone className="h-7 w-7 text-white" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 text-base mb-1">{t.title}</h3>
              <p className="text-sm text-gray-600 mb-3">{t.description}</p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {t.features.map((feature, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                    <Check className="h-3 w-3" />
                    {feature}
                  </span>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleInstall}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t.install}
                </Button>
                <Button 
                  onClick={handleDismiss}
                  variant="outline"
                  size="sm"
                >
                  {t.notNow}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Hook to manually trigger install prompt
export const usePWAInstall = () => {
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return false;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    setCanInstall(false);
    
    return outcome === 'accepted';
  };

  return { canInstall, promptInstall };
};

export default PWAInstallPrompt;
